interface WeeklyPracticeTrackerProps {
  activeDays: string[]; // Array of ISO date strings: ["2025-12-01", "2025-12-03"]
}

export default function WeeklyPracticeTracker({ activeDays }: WeeklyPracticeTrackerProps) {
  // Get current week (Monday to Sunday)
  const today = new Date();
  const currentDayOfWeek = today.getDay(); // 0 = Sunday, 1 = Monday, etc.
  const daysFromMonday = currentDayOfWeek === 0 ? 6 : currentDayOfWeek - 1; // Adjust so Monday = 0

  const monday = new Date(today);
  monday.setDate(today.getDate() - daysFromMonday);
  monday.setHours(0, 0, 0, 0);

  // Generate array of 7 days (Mon-Sun)
  const weekDays = Array.from({ length: 7 }, (_, i) => {
    const date = new Date(monday);
    date.setDate(monday.getDate() + i);
    return date;
  });

  // Helper to format date as YYYY-MM-DD in local timezone
  const formatLocalDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Convert activeDays to a Set for fast lookup
  const activeDaysSet = new Set(activeDays.map(d => d.split('T')[0])); // Normalize to YYYY-MM-DD

  // Count active days this week
  const activeCount = weekDays.filter(day => {
    const dateStr = formatLocalDate(day);
    return activeDaysSet.has(dateStr);
  }).length;

  const dayLabels = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

  return (
    <div className="bg-[#181818] rounded-2xl p-6 border border-[#282828]" style={{ boxShadow: '0px 12px 28px 3px rgba(0, 0, 0, 0.52), 0px 2px 8px 1px rgba(0, 0, 0, 0.16), inset 0px 1px 1px rgba(255, 255, 255, 0.04), inset 0px -1px 2px rgba(0, 0, 0, 0.22)' }}>
      <h3 className="text-[14px] font-semibold text-[#F5F5F5] mb-4">
        This week
      </h3>

      {/* Weekly tracker grid */}
      <div className="grid grid-cols-7 gap-1.5 mb-4">
        {weekDays.map((day, index) => {
          const dateStr = formatLocalDate(day);
          const isActive = activeDaysSet.has(dateStr);
          const isToday = dateStr === formatLocalDate(today);

          return (
            <div key={dateStr} className="flex flex-col items-center gap-1">
              <div className="text-[10px] text-[#808080] font-medium">
                {dayLabels[index]}
              </div>
              <div
                className={`w-9 h-9 rounded-lg transition-all ${
                  isActive
                    ? 'bg-[#40E0D0]'
                    : 'bg-transparent border border-[#282828]'
                } ${isToday ? 'ring-1 ring-[#40E0D0]/50' : ''}`}
                title={`${day.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}`}
              />
            </div>
          );
        })}
      </div>

      {/* Count display */}
      <p className="text-[13px] text-[#B0B0B0] text-center leading-relaxed">
        {activeCount === 0 && "You haven't shown up yet this week."}
        {activeCount === 1 && "You showed up once this week."}
        {(activeCount === 2 || activeCount === 3) && (
          <>You showed up <span className="text-[#40E0D0] font-semibold">{activeCount}</span> times this week.</>
        )}
        {(activeCount === 4 || activeCount === 5) && "You're showing up regularly this week."}
        {(activeCount === 6 || activeCount === 7) && "You've shown up most days this week."}
      </p>
    </div>
  );
}
